import clsx from "clsx"
import { FC, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { SpinnerCircular } from "spinners-react"
import { useAppSelector } from "store/index"
import { AppSelectors } from "store/slices/appSlice"
import { isUserInJail } from "store/apis/jailAPI/jail.helpers"
import { useGetMyJailStatusQuery } from "store/apis/jailAPI/jail.api"
import { useCheckBetaWhitelistQuery } from "store/apis/codBetaTestAPI"
import {
  userSelectors,
  useRefreshMyPositionInLeadersMutation
} from "store/apis/userAPI"
import { useModals } from "context/modals/hooks/useModals"
import { hapticFeedback } from "utils/hapticFeedback"
import {
  getTgWebApp,
  isTelegramEnv,
  safeClose,
  safeOpenTelegramLink
} from "utils/tgWebApp"
import { gearIcon } from "assets/icons"
import { Button } from "../@button"
import Container from "../container"
import SpaceNumber from "../spaceNumber"
import { profileData } from "./values"
import styles from "./index.module.scss"

interface ProfileProps {
  isPreview?: boolean
}

export const Profile: FC<ProfileProps> = ({ isPreview = false }) => {
  const { openModal } = useModals()
  const navigate = useNavigate()

  const {
    data: { username, dogname, location, positionInLeaders }
  } = useAppSelector(userSelectors.selectCurrentUser)

  const isUnder = useAppSelector(AppSelectors.selectIsUnder())

  const { data: jailStatus } = useGetMyJailStatusQuery()
  const jailData = jailStatus?.data
  const userIsInJail = isUserInJail(jailData)

  const dataKey = isUnder ? "under" : "common"
  const Data = profileData[dataKey]

  const [refreshPosition, { isLoading }] =
    useRefreshMyPositionInLeadersMutation()
  const { data: betaStatus } = useCheckBetaWhitelistQuery()

  useEffect(() => {
    if (isPreview || positionInLeaders > 0) {
      return
    } else {
      refreshPosition(location)
    }
  }, [positionInLeaders, isPreview])

  const Position = useMemo(() => {
    return (
      <span className={"my-position"}>
        <span>#</span>
        {isLoading ? (
          <SpinnerCircular
            className="loading-spinner"
            size={10}
            thickness={250}
            color="#35262699"
          />
        ) : (
          <SpaceNumber amount={positionInLeaders || 0} />
        )}
      </span>
    )
  }, [isLoading, positionInLeaders])

  const handleClickClashOfDiggers = () => {
    if (!betaStatus?.allowed) {
      openModal("clashOfDiggersBetaTest")
    } else {
      if (isTelegramEnv()) {
        getTgWebApp()?.unlockOrientation?.()
        getTgWebApp()?.requestFullscreen?.()
      }

      safeOpenTelegramLink(Data.cod.url)

      setTimeout(() => {
        safeClose()
      }, 0)
    }
  }

  const handleLeaderboard = () => {
    navigate(Data.leaderboard.url)
  }

  const handleArenaLeaderboard = () => {
    if (isUnder && "arenaLeaderboard" in Data) {
      navigate(Data.arenaLeaderboard.url)
    }
  }

  const handleClan = () => {
    navigate(Data.clan.url)
  }

  return (
    <div className={clsx(styles.Header)}>
      <Container
        className={clsx(styles.Header_profile)}
        id="profile"
        triangleTopPosLeft="calc(100% - 31px)"
        triangleBottomPosLeft="calc(100% - 137px)"
        onClick={() => {
          hapticFeedback()
          openModal("changeDogName")
        }}
      >
        <div className={clsx(styles.Header_profile_content)}>
          <h2 style={{ fontSize: dogname.length >= 6 ? "12px" : "14px" }}>
            {Position} <span>{dogname}</span>
          </h2>
          <h3>@{username}</h3>
        </div>
        <button className={clsx(styles.Header_profile_editName)}>
          <img src={gearIcon} width="10px" height="10px" />
        </button>
      </Container>
      <Button
        className={clsx(styles.Header_btn, {
          [styles.disable]: userIsInJail,
          [styles.yellow]: !isUnder,
          [styles.green]: isUnder
        })}
        onClick={handleLeaderboard}
        disabled={userIsInJail}
      >
        {typeof Data.leaderboard.icon === "function" ? (
          <Data.leaderboard.icon width="35" height="37" />
        ) : (
          <img src={Data.leaderboard.icon} />
        )}
      </Button>
      {isUnder && "arenaLeaderboard" in Data && (
        <Button
          className={clsx(styles.Header_btn, styles.pvp, {
            [styles.disable]: userIsInJail
          })}
          onClick={handleArenaLeaderboard}
          disabled={userIsInJail}
        >
          <img src={Data.arenaLeaderboard.icon} alt="PvP leaderboard" />
        </Button>
      )}
      <Button
        className={clsx(styles.Header_btn, {
          [styles.disable]: userIsInJail,
          [styles.orange]: !isUnder,
          [styles.purple]: isUnder
        })}
        onClick={handleClan}
        disabled={userIsInJail}
      >
        <img src={Data.clan.icon} />
      </Button>
      {!isUnder && (
        <Button
          className={clsx(styles.Header_btn, styles.green)}
          onClick={handleClickClashOfDiggers}
        >
          <img src={Data.cod.icon} />
        </Button>
      )}
    </div>
  )
}
